/**
 * Example prompts that surface every create-agent flow capability
 * next to the dashboard intake box.
 *
 * `flowId` matches creation_plan FLOW_SPECS ids (or close aliases the planner understands).
 */
export const CREATE_AGENT_PROMPT_RECOMMENDATIONS = [
  {
    id: 'unified-story',
    flowId: 'unified_story',
    flowLabel: 'Story',
    capability: 'Context, screenplay, then mixed scenes',
    prompt:
      'A father and daughter go on a weekend trip. Use the family photo I attach, keep their faces consistent, and mix a still scene with one talking avatar.',
  },
  {
    id: 'narration-bedtime',
    flowId: 'narration',
    flowLabel: 'Narrated story',
    capability: 'AI story + narrator VO',
    prompt:
      'Create a warm 3-minute Hindi bedtime story about two friends lost in a magical forest, Pixar cinematic style, 9:16 for Reels.',
  },
  {
    id: 'dialogue-ensemble',
    flowId: 'dialogue',
    flowLabel: 'Dialogue movie',
    capability: 'Multi-character voices',
    prompt:
      'Make a short dialogue film where a coach and a shy student argue then reconcile on a rainy train platform — two characters with distinct voices.',
  },
  {
    id: 'script-verbatim',
    flowId: 'script_based',
    flowLabel: 'Script → video',
    capability: 'Verbatim script scenes',
    prompt:
      'Turn this finished English script into narrated scenes without rewriting any lines — split by beats, Studio Ghibli look, 16:9.',
  },
  {
    id: 'web-scraper-facts',
    flowId: 'script_based',
    flowLabel: 'Web images',
    capability: 'Real photos from the web',
    prompt:
      'Explain the James Webb Space Telescope using real web photos only (not AI art). Search the web for high-resolution NASA and observatory images, then narrate a 2-minute explainer in English, 16:9.',
  },
  {
    id: 'song-mv',
    flowId: 'song_based',
    flowLabel: 'Music video',
    capability: 'Lyric sync + visuals',
    prompt:
      'I will upload a song — build a beat-synced music video from the lyrics with dreamlike surreal visuals, 9:16.',
  },
  {
    id: 'news-bulletin',
    flowId: 'news_video',
    flowLabel: 'News bulletin',
    capability: 'Anchor + newsroom tone',
    prompt:
      'Produce a 90-second news bulletin on India’s renewable energy milestones this year — clear newscaster voice, landscape 16:9.',
  },
  {
    id: 'storybuilder-pdf',
    flowId: 'storybuilder',
    flowLabel: 'PDF → video',
    capability: 'Page-by-page narration',
    prompt:
      'I will attach a children’s PDF storybook — narrate each page as a slideshow video with soft storyteller voice and subtitles.',
  },
  {
    id: 'reels-shorts',
    flowId: 'reels_shorts',
    flowLabel: 'Reels & Shorts',
    capability: 'AI cut + B-roll',
    prompt:
      'I will upload talking-head clips — auto-transcribe, tight AI edit for Instagram Reels, balanced dead-air cleanup, vertical 9:16 with B-roll overlays.',
  },
  {
    id: 'pitch-teleprompter',
    flowId: 'pitch_recording',
    flowLabel: 'Pitch recording',
    capability: 'Teleprompter take',
    prompt:
      'Help me record an investor pitch with teleprompter — sections for problem, solution, traction, and ask. Camera on, no screen share.',
  },
  {
    id: 'tutorial-lesson',
    flowId: 'script_audio_align',
    flowLabel: 'Tutorial',
    capability: 'Lesson + slide align',
    prompt:
      'Teach beginners how React hooks work — outline concepts, write clear narration, align audio to slides, medium length, informative tone.',
  },
  {
    id: 'mascot-brand',
    flowId: 'mascot_animation',
    flowLabel: 'Mascot',
    capability: 'Photo → animated mascot',
    prompt:
      'Animate our brand mascot from reference photos — create a short transparent bumper for the YouTube channel intro.',
  },
  {
    id: 'ad-spot',
    flowId: 'advertisement',
    flowLabel: 'Advertisement',
    capability: 'Hook + CTA spot',
    prompt:
      'Write and produce a punchy 30-second product ad for a hydration bottle — hook, benefit, CTA, comedy tone, 9:16 for Shorts.',
  },
  {
    id: 'narration-scrape-news',
    flowId: 'narration',
    flowLabel: 'Fact video',
    capability: 'Scraped stills + narration',
    prompt:
      'Make a factual vertical video about recent cricket World Cup highlights using real web images only — search for match photos, Hindi narration, 9:16.',
  },
  {
    id: 'dialogue-kids',
    flowId: 'dialogue',
    flowLabel: 'Kids dialogue',
    capability: 'Character conversation',
    prompt:
      'A gentle kids dialogue story where a cat and a puppy learn to share toys — soft watercolor storybook look, short length, Hindi.',
  },
  {
    id: 'script-web-product',
    flowId: 'script_based',
    flowLabel: 'Product explainer',
    capability: 'Web scrape product shots',
    prompt:
      'Use real product photos from the web (not AI drawings) to explain how an electric scooter works — search for cutaway and lifestyle images, English, medium length.',
  },
  {
    id: 'reels-podcast',
    flowId: 'reels_shorts',
    flowLabel: 'Clip podcast',
    capability: 'Podcast edit mode',
    prompt:
      'Edit these podcast clips into a viral short — podcast editing mode, aggressive pause cleanup, captions, 9:16.',
  },
  {
    id: 'tutorial-presentation',
    flowId: 'script_audio_align',
    flowLabel: 'Presentation',
    capability: 'Deck-style lesson',
    prompt:
      'Turn this topic into a boardroom presentation video: “Quarterly OKRs for a SaaS startup” — outline, narration, 16:9.',
  },
  {
    id: 'ad-brand',
    flowId: 'advertisement',
    flowLabel: 'Brand spot',
    capability: 'Social campaign ad',
    prompt:
      'Create a 45-second brand awareness ad for an eco-friendly detergent — motivational tone, photoreal cinematic, 1:1 for Instagram feed.',
  },
];

export function createAgentPromptById(id) {
  return CREATE_AGENT_PROMPT_RECOMMENDATIONS.find((row) => row.id === id) || null;
}
